import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@shared/components/ui/button';
import { ENV } from '@shared/config/env';
import {
  X,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  LayoutDashboard,
  FolderKanban,
  ClipboardCheck,
  QrCode,
  User,
  Upload,
  Search,
  Printer,
  BookOpen,
  Shield,
  LogIn,
  UserPlus,
} from 'lucide-react';

const PortalHelpModal = ({ isOpen, onClose }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Help & User Guide - Supplier Portal</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #333; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            h2 { font-size: 18px; margin-top: 24px; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #e5e7eb; }
            h3 { font-size: 14px; margin-top: 12px; margin-bottom: 4px; }
            p { font-size: 13px; color: #555; margin-bottom: 8px; }
            .section { margin-bottom: 20px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>Help & User Guide</h1>
          <p style="color: #888;">Supplier Portal - Site Access</p>
    `);
    sections.forEach(section => {
      printWindow.document.write(`<div class="section"><h2>${section.title}</h2>`);
      section.content.forEach(item => {
        printWindow.document.write(`<h3>${item.heading}</h3><p>${item.text}</p>`);
      });
      printWindow.document.write('</div>');
    });
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const allSections = useMemo(() => [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: LogIn,
      content: [
        { heading: 'Accessing the Portal', text: 'You will receive an email invitation with a link to the supplier portal. Click the link to open the login page.' },
        { heading: 'Logging In', text: 'Enter your email address and click Send Code. A 6-digit verification code will be sent to your email. Enter the code to access the portal.' },
        { heading: 'Paste Support', text: 'You can paste the 6-digit code directly from your email — each digit will automatically fill into the correct box.' },
        { heading: 'Session', text: 'Your session remains active while you\'re using the portal. If you close the browser, you may need to log in again with a new code.' },
      ]
    },
    {
      id: 'profile',
      title: 'Your Profile',
      icon: User,
      content: [
        { heading: 'Profile Requirement', text: 'You must complete your profile before accessing other portal features. You\'ll be prompted to do this on your first login.' },
        { heading: 'Required Information', text: 'Fill in your full name, email, phone number, company name, and select the site/depot you\'re attending.' },
        { heading: 'Profile Photo', text: 'Upload a photo from your device or take a selfie using the camera button. Photos must be under 2MB.' },
        { heading: 'Vehicle Registration', text: 'If you will be driving to site, enter your vehicle registration number.' },
        { heading: 'Editing', text: 'You can update your profile at any time from the Dashboard by clicking the edit icon on your profile card.' },
      ]
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      content: [
        { heading: 'Overview', text: 'The dashboard shows your profile card, a summary of your completed inductions, and your project involvement.' },
        { heading: 'Profile Card', text: 'View your name, company, email, phone, photo, vehicle registration, and site assignment at a glance.' },
        { heading: 'Induction Status', text: 'A table shows all your induction briefs with completion dates, expiry dates, and status indicators (green = verified, yellow = pending, red = expired).' },
        { heading: 'Quick Navigation', text: 'Use the navigation links to jump to Inductions, Projects, or your Digital Badge.' },
      ]
    },
    {
      id: 'inductions',
      title: 'Completing Inductions',
      icon: BookOpen,
      content: [
        { heading: 'Viewing Briefs', text: 'The Inductions page shows all induction briefs assigned to you based on your site/depot. Each brief shows its completion status.' },
        { heading: 'Brief Types', text: 'Briefs can be questions you need to answer, PDF documents to read through, or safety videos to watch. You must engage with all content before submitting.' },
        { heading: 'Answering Questions', text: 'For question-based briefs, work through each section: text answers, checkboxes, radio buttons, or dropdown selections. Some questions may be required.' },
        { heading: 'Electronic Signature', text: 'At the end of each brief, you must provide your electronic signature using the signature pad. Draw your signature with your finger (mobile) or mouse (desktop). Use Clear to start again.' },
        { heading: 'Submitting', text: 'Once you\'ve reviewed all content and signed, click Submit. Your submission will be reviewed by the site administrator.' },
        { heading: 'Mandatory Briefs', text: 'Some briefs are marked as mandatory. You must complete all mandatory briefs before you can generate your Digital Badge and QR pass for site access.' },
      ]
    },
    {
      id: 'projects',
      title: 'Projects',
      icon: FolderKanban,
      content: [
        { heading: 'Viewing Projects', text: 'The Projects page lists all projects you\'re involved in, either as a Project Lead or Team Member. Your role is shown on each project card.' },
        { heading: 'Project Status', text: 'Each project has a status: Awaiting Setup (new), In Progress (documents being uploaded), Submitted (sent for review), Approved, Rejected, Active, or Completed.' },
        { heading: 'Creating a Project', text: 'If you\'re a Project Lead, click "Create Project" to start a new project. Enter the project name, summary, select the site, and set start/end dates.' },
        { heading: 'Project Detail', text: 'Click "View / Manage" on a project to see full details, upload documents, manage team members, and submit for approval.' },
      ]
    },
    {
      id: 'documents',
      title: 'Uploading Documents',
      icon: Upload,
      content: [
        { heading: 'Document Types', text: 'You can upload: RAMS (Risk Assessment & Method Statement), Method Statements, Insurance Certificates, Work Permits, Site Photos, and other supporting documents.' },
        { heading: 'Uploading', text: 'In your project detail page, click "Upload Document". Select the document type, choose the file (drag & drop supported), and optionally add version notes.' },
        { heading: 'Supported Formats', text: 'PDF, Word documents (.doc, .docx), and images (JPG, PNG) are supported. Maximum file size is 25MB.' },
        { heading: 'Document Status', text: 'After upload, documents show as "Pending" until reviewed. The admin will mark each as "Accepted" or "Rejected". Rejected documents can be re-uploaded.' },
        { heading: 'Re-uploading', text: 'If a document is rejected, upload a corrected version. The previous version is replaced.' },
      ]
    },
    {
      id: 'team-members',
      title: 'Team Management',
      icon: UserPlus,
      content: [
        { heading: 'Inviting Members', text: 'As Project Lead, click "Invite Team Member" in your project detail page. Enter the person\'s name, email, phone, company, and their role on the project.' },
        { heading: 'Invitation Email', text: 'Invited team members receive an email with an access code and link to the portal where they can complete their profile and inductions.' },
        { heading: 'Tracking Progress', text: 'View each team member\'s induction completion status in the project detail. A green tick means their induction is complete; pending shows they still need to finish.' },
        { heading: 'Removing Members', text: 'If someone is no longer part of the project, use the delete button to remove them from the team.' },
        { heading: 'Submission Requirement', text: 'All team members must complete their site inductions before you can submit the project for review.' },
      ]
    },
    {
      id: 'submission',
      title: 'Submitting Projects',
      icon: ClipboardCheck,
      content: [
        { heading: 'When to Submit', text: 'Submit your project for review once all documents are uploaded and all team members have completed their inductions.' },
        { heading: 'Submit for Review', text: 'Click "Submit for Review" at the bottom of the project detail page. The site administrator will receive an email notification.' },
        { heading: 'Confirmation', text: 'A success popup confirms your submission and lets you return to the projects list or stay on the project page.' },
        { heading: 'Re-submission', text: 'If the admin requests changes or rejects documents, you can amend your submission and click "Update & Resubmit" to send it for review again.' },
        { heading: 'After Approval', text: 'Once approved, QR passes are automatically generated for you and all team members. You\'ll receive an email with your QR code for site access.' },
      ]
    },
    {
      id: 'credentials',
      title: 'Credentials',
      icon: Shield,
      content: [
        { heading: 'What are Credentials?', text: 'Credentials are professional certificates and cards that verify your qualifications, such as Medical Self-Certificates or CSCS/ECS cards.' },
        { heading: 'Uploading', text: 'Drag and drop your certificate files (PDF, JPG, PNG) into the upload area, or click to browse for files.' },
        { heading: 'Verification', text: 'Uploaded credentials are reviewed by site administrators. The verification panel shows counts: Verified, Pending, and Required.' },
        { heading: 'Status', text: 'Green = Verified, Yellow = Pending review, Red = Required but not yet uploaded.' },
      ]
    },
    {
      id: 'digital-badge',
      title: 'Digital Badge & QR Pass',
      icon: QrCode,
      content: [
        { heading: 'What is the Digital Badge?', text: 'Your Digital Badge contains a QR code that you scan at the site kiosk to sign in and out. It\'s your electronic site access pass.' },
        { heading: 'Generating Your Badge', text: 'Once all mandatory induction briefs are complete, a "Generate Digital Badge" button appears. Click it to create your pass.' },
        { heading: 'Badge Information', text: 'Your badge shows: your QR code, pass token (which you can copy), validity dates, and badge status.' },
        { heading: 'Using at Kiosk', text: 'At the site entry kiosk, present your QR code to the camera. Confirm the fitness attestation (fit for work & PPE acknowledged) to complete check-in.' },
        { heading: 'Checking Out', text: 'When leaving site, scan your QR code at the kiosk in check-out mode to record your departure.' },
        { heading: 'Locked State', text: 'If mandatory briefs are incomplete, the badge shows a locked state with a list of pending briefs. Complete them first via the Inductions page.' },
      ]
    },
  ], []);

  // Filter sections by search query
  const sections = useMemo(() => {
    if (!searchQuery.trim()) return allSections;
    const query = searchQuery.toLowerCase();
    return allSections.filter(section => {
      if (section.title.toLowerCase().includes(query)) return true;
      return section.content.some(
        item =>
          item.heading.toLowerCase().includes(query) ||
          item.text.toLowerCase().includes(query)
      );
    });
  }, [allSections, searchQuery]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <HelpCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Help & User Guide</h2>
              <p className="text-sm text-muted-foreground">Supplier Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={handlePrint} title="Print guide">
              <Printer className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {sections.length} {sections.length === 1 ? 'topic' : 'topics'} found
            </p>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-4">
          {sections.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No topics found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sections.map((section) => (
                <div key={section.id} className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <section.icon className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{section.title}</span>
                    </div>
                    {expandedSection === section.id ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {expandedSection === section.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-4 border-t border-border bg-muted/20">
                          <div className="pt-4 space-y-3">
                            {section.content.map((item, index) => (
                              <div key={index}>
                                <h4 className="font-medium text-sm text-foreground">{item.heading}</h4>
                                <p className="text-sm text-muted-foreground mt-1">{item.text}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}

          {/* Quick Reference sections only when not searching */}
          {!searchQuery && (
            <>
              {/* Typical Workflow */}
              <div className="mt-6 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                <h3 className="font-medium text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" />
                  Your Typical Workflow
                </h3>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Complete your <strong className="text-foreground">Profile</strong> (name, company, photo, site)</li>
                  <li>Complete all <strong className="text-foreground">Induction Briefs</strong> for your site</li>
                  <li>Generate your <strong className="text-foreground">Digital Badge</strong> (QR pass)</li>
                  <li>Upload <strong className="text-foreground">Documents</strong> to your project (RAMS, insurance, etc.)</li>
                  <li>Invite <strong className="text-foreground">Team Members</strong> and wait for their inductions</li>
                  <li><strong className="text-foreground">Submit</strong> the project for admin review</li>
                  <li>Receive <strong className="text-foreground">QR Pass</strong> email upon approval — scan at kiosk to enter site</li>
                </ol>
              </div>

              {/* Quick Tips */}
              <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <h3 className="font-medium text-primary mb-2">Quick Tips</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Complete your profile first — it's required before you can do anything else</li>
                  <li>• Complete all mandatory inductions to unlock your Digital Badge</li>
                  <li>• Upload all required documents before submitting your project for review</li>
                  <li>• Ensure all team members have completed inductions before you submit</li>
                  <li>• You can amend and resubmit if the admin requests changes</li>
                  <li>• Keep your QR code handy — you'll need it every time you enter or leave site</li>
                  <li>• Contact your site administrator if you need help or your pass has expired</li>
                </ul>
              </div>

              {/* Support */}
              <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
                <h3 className="font-medium mb-2">Need More Help?</h3>
                <p className="text-sm text-muted-foreground">
                  Contact the site administrator or email <span className="font-medium text-foreground">safety@apexiar.co.uk</span> for assistance with your account, inductions, or site access.
                </p>
              </div>

              {/* Version Info */}
              <div className="mt-4 text-center text-xs text-muted-foreground">
                Supplier Portal v{ENV.APP.VERSION} • Site Access Management
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30">
          <Button onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default PortalHelpModal;
